import type {
  SupportTicketChannel,
  SupportTicketCreate,
  SupportTicketLimits,
} from '@/services/supportTicketService';

/**
 * Formularzustand eines neuen Support-Tickets und seine Prüfung — rein, damit
 * testbar. Das Backend prüft dieselben Regeln noch einmal; hier geht es darum,
 * dass der Salon vor dem Absenden erfährt, was fehlt.
 */
export interface TicketDraft {
  channel: SupportTicketChannel | null;
  /** '' = weiß ich nicht / alle Nummern */
  agentNumber: string;
  conversationId: number | null;
  /** Ausdrücklich „kein Verlauf" gewählt → Zeitpunkt wird Pflicht. */
  noConversation: boolean;
  /** Wert eines <input type="datetime-local">, Ortszeit des Browsers. */
  occurredAt: string;
  category: string | null;
  description: string;
  expected: string;
  frequency: string | null;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  priceAccepted: boolean;
}

export type TicketField =
  | 'channel'
  | 'conversation'
  | 'occurredAt'
  | 'category'
  | 'description'
  | 'expected'
  | 'frequency'
  | 'contactName'
  | 'contactPhone'
  | 'contactEmail'
  | 'priceAccepted';

export type TicketErrors = Partial<Record<TicketField, string>>;

/** Reihenfolge im Formular — nach einem abgewiesenen Absenden springt der Fokus
 *  auf das erste fehlerhafte Feld. */
export const TICKET_FIELD_ORDER: TicketField[] = [
  'channel',
  'conversation',
  'occurredAt',
  'category',
  'description',
  'expected',
  'frequency',
  'contactName',
  'contactPhone',
  'contactEmail',
  'priceAccepted',
];

/**
 * Zeichen wie Pythons len(): Codepoints, nicht UTF-16-Einheiten. Sonst zählt
 * ein Emoji hier doppelt, das Backend lehnt ab und der Salon versteht nicht,
 * warum.
 */
export function charCount(value: string): number {
  return Array.from(value).length;
}

// Uhren von Browser und Server laufen nie exakt gleich.
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

// Nah an email-validator (Backend): Punkte nicht am Rand und nicht doppelt,
// Domain-Labels ohne Bindestrich am Rand, TLD aus mindestens zwei Buchstaben.
// Umlaut-Domains (IDN) sind erlaubt — das Backend akzeptiert sie auch.
const EMAIL_LOCAL_RE = /^[^\s@]+$/;
const EMAIL_DOMAIN_RE = /^(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+\p{L}{2,}$/u;

export function isPlausibleEmail(value: string): boolean {
  const at = value.lastIndexOf('@');
  if (at <= 0) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (!EMAIL_LOCAL_RE.test(local) || local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
    return false;
  }
  return EMAIL_DOMAIN_RE.test(domain);
}

export function emptyDraft(): TicketDraft {
  return {
    channel: null,
    agentNumber: '',
    conversationId: null,
    noConversation: false,
    occurredAt: '',
    category: null,
    description: '',
    expected: '',
    frequency: null,
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    priceAccepted: false,
  };
}

export function validateTicketDraft(
  draft: TicketDraft,
  limits: SupportTicketLimits,
  now: Date = new Date(),
): TicketErrors {
  const errors: TicketErrors = {};

  if (!draft.channel) errors.channel = 'Bitte wähle den betroffenen Agenten.';

  if (draft.conversationId === null) {
    if (!draft.noConversation) {
      errors.conversation = 'Bitte wähle das Gespräch aus – oder „Kein Verlauf vorhanden".';
    } else if (!draft.occurredAt) {
      errors.occurredAt = 'Bitte gib an, wann das Problem aufgetreten ist.';
    } else {
      const when = new Date(draft.occurredAt).getTime();
      if (Number.isNaN(when)) errors.occurredAt = 'Ungültiges Datum.';
      else if (when > now.getTime() + FUTURE_TOLERANCE_MS) errors.occurredAt = 'Der Zeitpunkt liegt in der Zukunft.';
    }
  }

  if (!draft.category) errors.category = 'Bitte wähle, was schiefgelaufen ist.';

  const description = charCount(draft.description.trim());
  if (description < limits.description_min) {
    errors.description = `Bitte beschreibe das Problem mit mindestens ${limits.description_min} Zeichen (aktuell ${description}) – je genauer, desto schneller finden wir die Ursache.`;
  } else if (description > limits.description_max) {
    errors.description = `Bitte höchstens ${limits.description_max} Zeichen (aktuell ${description}).`;
  }

  const expected = charCount(draft.expected.trim());
  if (expected < limits.expected_min) {
    errors.expected = `Bitte beschreibe mit mindestens ${limits.expected_min} Zeichen, was der Agent hätte tun sollen (aktuell ${expected}).`;
  } else if (expected > limits.expected_max) {
    errors.expected = `Bitte höchstens ${limits.expected_max} Zeichen (aktuell ${expected}).`;
  }

  if (!draft.frequency) errors.frequency = 'Bitte wähle, wie oft das passiert.';

  if (charCount(draft.contactName.trim()) < 2) errors.contactName = 'Bitte gib einen Ansprechpartner an.';
  if (draft.contactPhone.replace(/\D/g, '').length < 5) {
    errors.contactPhone = 'Bitte gib eine Rückrufnummer an.';
  }

  const email = draft.contactEmail.trim();
  if (email && (!isPlausibleEmail(email) || charCount(email) > limits.contact_email_max)) {
    errors.contactEmail = 'Bitte eine gültige E-Mail-Adresse angeben – oder das Feld leer lassen.';
  }

  if (!draft.priceAccepted) errors.priceAccepted = 'Bitte bestätige den Pauschalpreis.';

  return errors;
}

export function toCreatePayload(draft: TicketDraft, acceptedPriceEur: number): SupportTicketCreate {
  const withConversation = draft.conversationId !== null;
  return {
    channel: draft.channel as SupportTicketChannel,
    agent_number: draft.agentNumber.trim() || null,
    category: draft.category as string,
    conversation_id: withConversation ? draft.conversationId : null,
    // datetime-local ist Ortszeit ohne Zone — als Zeitpunkt mit Zone senden,
    // sonst müsste das Backend raten, ob Berlin oder UTC gemeint ist.
    occurred_at: !withConversation && draft.occurredAt ? new Date(draft.occurredAt).toISOString() : null,
    description: draft.description.trim(),
    expected_behavior: draft.expected.trim(),
    frequency: draft.frequency as string,
    contact_name: draft.contactName.trim(),
    contact_phone: draft.contactPhone.trim(),
    contact_email: draft.contactEmail.trim() || null,
    accepted_price_eur: acceptedPriceEur,
    price_accepted: true,
  };
}
